/**
 * Offline action queue.
 *
 * Mutations that fail because the network is offline are enqueued in
 * IndexedDB and replayed automatically when the browser regains
 * connectivity, the tab regains focus, or the user clicks "Retry now".
 *
 * Optimistic UI in the calling hook is preserved — this module just owns
 * the durable retry. Handlers are registered once at app boot.
 */

import { toast } from "sonner";

export type QueueKind =
  | "saved_episode"
  | "episode_note"
  | "saved_topic"
  | "resume_position"
  | "playlist_item";

export interface QueuedAction {
  id?: number;
  kind: QueueKind;
  payload: unknown;
  attempts: number;
  createdAt: number;
  /** Coalescing key — newer enqueues replace older queued actions sharing this key. */
  dedupKey?: string;
}

const DB_NAME = "atp-offline-queue";
const STORE = "outbox";
const MAX_ATTEMPTS = 5;
const CHANGE_EVENT = "lovable:queue-changed";

type Handler = (payload: unknown) => Promise<void>;
const handlers = new Map<QueueKind, Handler>();
let flushing = false;
let initialized = false;

export function registerQueueHandler(kind: QueueKind, handler: Handler) {
  handlers.set(kind, handler);
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        store.createIndex("dedupKey", "dedupKey", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => T | Promise<T>): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result as T);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function emitChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }
}

export async function pendingCount(): Promise<number> {
  if (typeof indexedDB === "undefined") return 0;
  try {
    return await withStore("readonly", (s) => new Promise<number>((res) => {
      const r = s.count();
      r.onsuccess = () => res(r.result);
      r.onerror = () => res(0);
    }));
  } catch {
    return 0;
  }
}

export async function enqueue(action: Omit<QueuedAction, "attempts" | "createdAt" | "id">) {
  if (typeof indexedDB === "undefined") return;
  try {
    await withStore("readwrite", async (store) => {
      if (action.dedupKey) {
        // Remove any earlier queued action with the same dedup key — keep newest only.
        const idx = store.index("dedupKey");
        await new Promise<void>((res) => {
          const req = idx.openCursor(IDBKeyRange.only(action.dedupKey));
          req.onsuccess = () => {
            const cursor = req.result;
            if (cursor) { cursor.delete(); cursor.continue(); } else res();
          };
          req.onerror = () => res();
        });
      }
      store.add({ ...action, attempts: 0, createdAt: Date.now() } as QueuedAction);
    });
    emitChange();
    if (typeof navigator !== "undefined" && navigator.onLine) {
      void flush();
    }
  } catch (e) {
    console.warn("[offlineQueue] enqueue failed", e);
  }
}

async function readAll(): Promise<QueuedAction[]> {
  return withStore("readonly", (s) => new Promise<QueuedAction[]>((res) => {
    const r = s.getAll();
    r.onsuccess = () => res(r.result as QueuedAction[]);
    r.onerror = () => res([]);
  }));
}

async function update(action: QueuedAction) {
  await withStore("readwrite", (s) => s.put(action));
}

async function remove(id: number) {
  await withStore("readwrite", (s) => s.delete(id));
}

export async function flush(): Promise<{ sent: number; failed: number }> {
  if (flushing) return { sent: 0, failed: 0 };
  if (typeof navigator !== "undefined" && !navigator.onLine) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    const items = (await readAll()).sort((a, b) => a.createdAt - b.createdAt);
    for (const item of items) {
      const handler = handlers.get(item.kind);
      if (!handler) continue;
      try {
        await handler(item.payload);
        await remove(item.id!);
        sent++;
      } catch (e) {
        const attempts = (item.attempts || 0) + 1;
        if (attempts >= MAX_ATTEMPTS) {
          await remove(item.id!);
          failed++;
          console.warn("[offlineQueue] dropping after max attempts", item.kind, e);
        } else {
          await update({ ...item, attempts });
          // Stop iterating — likely still offline or rate-limited.
          break;
        }
      }
    }
    if (sent > 0) {
      toast.success(`Synced ${sent} change${sent === 1 ? "" : "s"} you made offline`);
    }
    if (failed > 0) {
      toast.error(`${failed} offline change${failed === 1 ? "" : "s"} couldn't be saved`);
    }
    emitChange();
  } finally {
    flushing = false;
  }
  return { sent, failed };
}

export function initOfflineQueue() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("online", () => { void flush(); });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine) void flush();
  });
  // Best-effort initial drain.
  if (navigator.onLine) void flush();
}

export const QUEUE_CHANGE_EVENT = CHANGE_EVENT;

/** True for fetch failures that mean "the network is gone", not "the server said no". */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  if (!err) return false;
  const msg = (err as { message?: string })?.message?.toLowerCase() || "";
  return (
    err instanceof TypeError ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  );
}
